import type { PopoverOrigin } from "@mui/material";
import type { TFunction } from "i18next";

import appConfig from "../../config/appConfig";
import { MenuListPositionOpts } from "../../shared/types/componentTypes";

export const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

export const generateFakeId = () => {
	let text = "";
	const possible = "abcdefghijklmnopqrstuvwxyz0123456789";

	for (let i = 0; i < 5; i++) {
		text += possible.charAt(Math.floor(Math.random() * possible.length));
	}

	return text;
};

export const uuidv4 = (): string => {
	// @ts-expect-error: special code
	return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
		// @ts-expect-error: special code
		(c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
	);
};

export const makeId = (length = 5) => {
	let result = "";
	const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	const charactersLength = characters.length;
	let counter = 0;
	while (counter < length) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
		counter += 1;
	}
	return result;
};

export function* generateSequence(start, end) {
	for (let i = start; i <= end; i++) yield i;
}

export function randomInteger(min, max) {
	return Math.floor(Math.random() * (max - min)) + min;
}

const safelyParseJSON = <T>(json): T => {
	let parsed;

	try {
		parsed = JSON.parse(json);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
	} catch (error) {
		// empty: nothing to do here
	}

	return parsed as T;
};

const numberWithCommas = (x: number | string): string => {
	if (x) {
		const parts = x.toString().split(".");
		if (parts?.length && parts[0]) {
			parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
			return parts.join(".");
		}
		return x.toString();
	}
	return "";
};

const formatCount = (n: number, singular: string, multiple?: string): string => {
	if (n === 0) return `No ${multiple || singular + "s"}`;
	if (n === 1) return `${n} ${singular}`;

	if (n < 1e3) return `${n} ${multiple || singular + "s"}`;
	if (n >= 1e3 && n < 1e6) return +(n / 1e3).toFixed(1) + `K ${multiple || singular + "s"}`;
	if (n >= 1e6 && n < 1e9) return +(n / 1e6).toFixed(1) + `M ${multiple || singular + "s"}`;

	return `${n}`;
};

const isMobileSafari = () => {
	const isIOS =
		(/iPad|iPhone|iPod/.test(navigator.platform) ||
			(navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) &&
		!(window as any).MSStream;
	return isIOS && navigator.userAgent.match(/AppleWebKit/);
};

const uniqBy = <T>(array: T[], key: keyof T) => {
	const seen = new Set();
	return array.filter(item => {
		const el = item[key];
		return seen.has(el) ? false : seen.add(el);
	});
};

export const getRandomInt = (min: number, max: number) => {
	const minVal = Math.ceil(min);
	const maxVal = Math.floor(max);
	return Math.floor(Math.random() * (maxVal - minVal + 1)) + minVal;
};

export const capitalize = (text: string) => {
	return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const compareObjects = (o1: any, o2: any) => {
	for (const p in o1) {
		if (o1.hasOwnProperty(p)) {
			if (o1[p] !== o2[p]) {
				return false;
			}
		}
	}
	for (const p in o2) {
		if (o2.hasOwnProperty(p)) {
			if (o1[p] !== o2[p]) {
				return false;
			}
		}
	}
	return true;
};

export const groupBy = (xs: any[], key: string) =>
	xs.reduce((rv, x) => {
		(rv[x[key]] = rv[x[key]] || []).push(x);
		return rv;
	}, {});

export { safelyParseJSON, numberWithCommas, formatCount, isMobileSafari, uniqBy };

export const getMonthOptions = (t: TFunction) =>
	t
		? [
				{ label: t("date:months.january"), value: 1 },
				{ label: t("date:months.february"), value: 2 },
				{ label: t("date:months.march"), value: 3 },
				{ label: t("date:months.april"), value: 4 },
				{ label: t("date:months.may"), value: 5 },
				{ label: t("date:months.june"), value: 6 },
				{ label: t("date:months.july"), value: 7 },
				{ label: t("date:months.august"), value: 8 },
				{ label: t("date:months.september"), value: 9 },
				{ label: t("date:months.october"), value: 10 },
				{ label: t("date:months.november"), value: 11 },
				{ label: t("date:months.december"), value: 12 }
			]
		: [];

export const getDaysOfMonthOptions = (month = 1) => {
	let daysCount = 31;
	const dayList: { label: string; value: number }[] = [];
	if ((month <= 7 && month % 2 === 0) || (month >= 7 && month % 2 !== 0)) {
		daysCount = 30;
	}
	if (month === 2) {
		daysCount = 28;
	}
	for (let i = 0; i < daysCount; i++) {
		dayList.push({ label: `${i + 1}`, value: i + 1 });
	}
	return dayList;
};

export const getYearOptions = (backToYears = 100) => {
	const currentYear = new Date().getFullYear();
	const yearList: { label: string; value: number }[] = [];
	for (let i = currentYear; i >= currentYear - backToYears; i--) {
		yearList.push({ label: `${i}`, value: i });
	}
	return yearList;
};

export const getCorrectGenderValList = (t: TFunction): { label: string; value: string }[] => [
	{
		label: t("male"),
		value: "m"
	},
	{
		label: t("female"),
		value: "f"
	}
];

export const toggleFullscreen = (event: HTMLElement) => {
	let element = document.body;

	if (event instanceof HTMLElement) {
		element = event;
	}

	if (
		!document.fullscreenElement &&
		// @ts-expect-error: crossbrowser support
		!document.webkitFullscreenElement &&
		// @ts-expect-error: crossbrowser support
		!document.mozFullScreenElement &&
		// @ts-expect-error: crossbrowser support
		!document.msFullscreenElement
	) {
		// Enter fullscreen
		if (element.requestFullscreen) {
			element.requestFullscreen();
			// @ts-expect-error: crossbrowser support
		} else if (element.mozRequestFullScreen) {
			// Firefox
			// @ts-expect-error: crossbrowser support
			element.mozRequestFullScreen();
			// @ts-expect-error: crossbrowser support
		} else if (element.webkitRequestFullscreen) {
			// Chrome, Safari, Opera
			// @ts-expect-error: crossbrowser support
			element.webkitRequestFullscreen();
			// @ts-expect-error: crossbrowser support
		} else if (element.msRequestFullscreen) {
			// IE/Edge
			// @ts-expect-error: crossbrowser support
			element.msRequestFullscreen();
		}
	} else {
		// Exit fullscreen
		if (document.exitFullscreen) {
			document.exitFullscreen();
			// @ts-expect-error: crossbrowser support
		} else if (document.mozCancelFullScreen) {
			// Firefox
			// @ts-expect-error: crossbrowser support
			document.mozCancelFullScreen();
			// @ts-expect-error: crossbrowser support
		} else if (document.webkitExitFullscreen) {
			// Chrome, Safari, Opera
			// @ts-expect-error: crossbrowser support
			document.webkitExitFullscreen();
			// @ts-expect-error: crossbrowser support
		} else if (document.msExitFullscreen) {
			// IE/Edge
			// @ts-expect-error: crossbrowser support
			document.msExitFullscreen();
		}
	}
};

export const getAnchorOriginPos = (contentPosition: MenuListPositionOpts): PopoverOrigin => {
	switch (contentPosition) {
		case MenuListPositionOpts.TOP_LEFT:
			return { vertical: "top", horizontal: "left" };

		case MenuListPositionOpts.TOP_RIGHT:
			return { vertical: "top", horizontal: "right" };

		case MenuListPositionOpts.TOP_CENTER:
			return { vertical: "top", horizontal: "center" };

		case MenuListPositionOpts.BOTTOM_LEFT:
			return { vertical: "bottom", horizontal: "left" };

		case MenuListPositionOpts.BOTTOM_RIGHT:
			return { vertical: "bottom", horizontal: "right" };

		default:
			return {
				vertical: "bottom",
				horizontal: "center"
			};
	}
};

export const getTransformOriginPos = (popoverPosition): PopoverOrigin => {
	switch (popoverPosition) {
		case MenuListPositionOpts.TOP_LEFT:
			return { vertical: "top", horizontal: "left" };

		case MenuListPositionOpts.TOP_RIGHT:
			return { vertical: "top", horizontal: "right" };

		case MenuListPositionOpts.TOP_CENTER:
			return { vertical: "top", horizontal: "center" };

		case MenuListPositionOpts.BOTTOM_LEFT:
			return { vertical: "bottom", horizontal: "left" };

		case MenuListPositionOpts.BOTTOM_RIGHT:
			return { vertical: "bottom", horizontal: "right" };

		default:
			return {
				vertical: "bottom",
				horizontal: "center"
			};
	}
};

export const generateUniqueKey = (prefix = "key") => {
	return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const getProviderImageUrl = (params: { menuId?: string; image: string }) => {
	if (!params.menuId) {
		return "";
	}

	let providerImgPath = "";

	switch (params?.menuId?.toLowerCase()) {
		case "pragmaticplay":
		case "pragmaticplaylive":
			providerImgPath = "pragmatic-play.svg";
			break;

		case "boominggames":
			providerImgPath = "booming-games.svg";
			break;

		case "tomhorn":
			providerImgPath = "tomhorn-gaming.webp";
			break;

		case "amatic":
			providerImgPath = "amatic.svg";
			break;

		case "betsoft":
			providerImgPath = "https://agstatic.com/merchants/betsoft.jpg"; // TODO: update
			break;

		case "mg":
			providerImgPath = "https://agstatic.com/merchants/mg.jpg"; // TODO: update
			break;

		case "evosw":
			providerImgPath = "evolution.svg";
			break;

		default:
			break;
	}

	if (providerImgPath) {
		return providerImgPath.startsWith("https://")
			? providerImgPath
			: `${appConfig.GLOBAL_CONSTANTS.BLOCKASINO_ROOT_URL}/static/${providerImgPath}`;
	}

	if (params.image.startsWith("/gstatic")) {
		return `https://agstatic.com/${params.image.replace("/gstatic/", "")}`;
	}

	return "";
};
